import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/homebrewery.css';

// Simple logging utilities
const log = (...args) => console.log('[RulesTab]', ...args);
const warn = (...args) => console.warn('[RulesTab]', ...args);
const error = (...args) => console.error('[RulesTab]', ...args);

const RULE_LABELS = new Set(['Type', 'Use', 'Roll', 'Action']);

const splitHighlightedText = (text, query) => {
  const value = String(text || '');
  const term = String(query || '').trim();
  if (!term) return [value];
  const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return value.split(regex).filter(part => part !== '').map((part, index) => (
    part.toLowerCase() === term.toLowerCase()
      ? <mark key={`${part}-${index}`}>{part}</mark>
      : part
  ));
};

const parseRuleLines = (content) => String(content || '')
  .split('\n')
  .map(line => line.trim())
  .filter(Boolean)
  .map(line => {
    const match = line.match(/^([A-Za-z ]{2,18}):\s*(.*)$/);
    if (!match || !RULE_LABELS.has(match[1])) return { label: null, text: line };
    return { label: match[1], text: match[2] };
  });

function RuleContent({ content, query, compact = false }) {
  const lines = parseRuleLines(content || 'No content available.');
  const visibleLines = compact ? lines.slice(0, 3) : lines;
  const hiddenCount = Math.max(0, lines.length - visibleLines.length);

  return (
    <div className="phb-rule-content">
      {visibleLines.map((line, index) => (
        <div
          key={`${line.label || 'line'}-${index}`}
          className={line.label
            ? 'phb-rule-line phb-rule-line-labeled'
            : 'phb-rule-line phb-rule-line-plain'}
        >
          {line.label ? (
            <>
              <span className="phb-line-label">{line.label}</span>
              <span>{splitHighlightedText(line.text, query)}</span>
            </>
          ) : (
            <span>{splitHighlightedText(line.text, query)}</span>
          )}
        </div>
      ))}
      {compact && hiddenCount > 0 && (
        <div style={{ fontSize: '0.75rem', color: '#8b7355', fontStyle: 'italic' }}>
          +{hiddenCount} more line{hiddenCount === 1 ? '' : 's'}
        </div>
      )}
    </div>
  );
}

function RuleExample({ example, query, compact = false }) {
  if (!example) return null;
  const text = compact && example.length > 180
    ? `${example.slice(0, 180).replace(/\s+\S*$/, '')}...`
    : example;
  return (
    <div className="phb-example">
      <div className="phb-example-title">Example</div>
      <div className="phb-example-text">{splitHighlightedText(text, query)}</div>
    </div>
  );
}

function RulesTab({ authedPlayer, sessionId }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [browseCount, setBrowseCount] = useState(20);

  // Categories - load from backend when available
  const [ruleCategories, setRuleCategories] = useState([
    { id: 'all', name: 'All Rules' }
  ]);

  const buildHeaders = () => {
    const headers = { 'x-session-id': sessionId || '' };
    if (authedPlayer === 'gm') headers['x-gm-secret'] = process.env.REACT_APP_GM_SECRET || 'defaultsecret';
    return headers;
  };

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get('/api/rules/categories', { headers: buildHeaders() });
        if (resp && Array.isArray(resp.data) && resp.data.length) {
          setRuleCategories(resp.data);
        }
      } catch (e) {
        log('Could not fetch categories, using defaults', e && e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Popular/Quick access rules tuned for live play
  const quickRules = [
    'Dodge',
    'Parry',
    'Full Auto Burst',
    'Semi-Auto Burst',
    'Suppressing Fire',
    'Overwatch',
    'Pinning',
    'Righteous Fury',
    'Critical Damage',
    'Hordes',
    'Fear',
    'Cohesion Damage',
    'Maintaining Squad Mode',
    'Requisition',
    'Renown'
  ];

  const midgameCategories = [
    { id: 'actions', name: 'Actions' },
    { id: 'combat', name: 'Combat' },
    { id: 'damage', name: 'Damage' },
    { id: 'conditions', name: 'Conditions' },
    { id: 'hordes', name: 'Hordes' },
    { id: 'psychic', name: 'Psychic' },
    { id: 'skills', name: 'Skills' },
    { id: 'talents', name: 'Talents' },
    { id: 'squad_mode', name: 'Squad/Cohesion' },
    { id: 'requisition', name: 'Req/Renown' },
    { id: 'gm_tables', name: 'GM Tables' }
  ];

  useEffect(() => {
    loadRecentSearches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRecentSearches = () => {
    const saved = localStorage.getItem('dw:rules:recent');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (e) {
        warn('Failed to load recent searches:', e);
      }
    }
  };

  const saveRecentSearch = (query) => {
    const updated = [query, ...recentSearches.filter(q => q !== query)].slice(0, 10);
    setRecentSearches(updated);
    localStorage.setItem('dw:rules:recent', JSON.stringify(updated));
  };

  const searchRules = async (query, category = selectedCategory) => {
    if (!query.trim()) return;

    setLoading(true);
    try {
      log('Searching rules:', query, 'category:', category);

      const params = new URLSearchParams({
        q: query.trim(),
        category: category !== 'all' ? category : '',
        midgame: 'true',
        limit: '30'
      });

      const response = await axios.get(`/api/rules/search?${params}`, {
        headers: buildHeaders()
      });

      if (response.data && Array.isArray(response.data)) {
        setSearchResults(response.data);
        saveRecentSearch(query.trim());
      } else {
        setSearchResults([]);
        warn('No search results returned');
      }
    } catch (err) {
      error('Search failed:', err);
      setSearchResults([]);

      setSearchResults([
        {
          id: 'example_1',
          title: 'Rules Search Not Available',
          content: 'The rules database is not yet populated. This feature requires processing the PDF rulebooks.',
          category: 'system',
          page: null,
          source: 'System'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchRules(searchQuery);
  };

  const handleQuickSearch = (rule) => {
    setSearchQuery(rule);
    searchRules(rule);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (searchQuery.trim()) {
      searchRules(searchQuery, category);
    }
  };

  const openRule = async (rule) => {
    setSelectedRule(rule);
    try {
      const resp = await axios.get(`/api/rules/rule/${rule.rule_id || rule.id}`, { headers: buildHeaders() });
      if (resp && resp.data && resp.data.content) {
        setSelectedRule(prev => prev ? { ...prev, ...resp.data } : resp.data);
      }
    } catch (e) {
      warn('Could not fetch full rule:', e && e.message);
    }
  };

  const fetchRandom = async (category, count = browseCount) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ count: String(count), category: category !== 'all' ? category : '', midgame: 'true' });
      const resp = await axios.get(`/api/rules/random?${params}`, { headers: buildHeaders() });
      if (resp && Array.isArray(resp.data)) setSearchResults(resp.data);
    } catch (e) {
      warn('Browse failed', e && e.message);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="phb-page">
      <div style={{ maxWidth: '72rem', margin: '0 auto' }}>
        {/* Header */}
        <h1 className="phb-h1">Deathwatch Rules</h1>
        <p className="phb-text" style={{ textAlign: 'center', fontStyle: 'italic', color: '#4a3728', marginBottom: '1.5rem' }}>
          Search and reference rules from the Core Rulebook and GM's Kit
        </p>

        {/* Search Section */}
        <div className="phb-search-bar">
          <form onSubmit={handleSearch}>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for rules, weapons, talents, skills..."
                  className="phb-search-input"
                  style={{ width: '100%' }}
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="phb-search-select"
              >
                {ruleCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  type="submit"
                  disabled={loading || !searchQuery.trim()}
                  className="phb-btn phb-btn-primary"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    setLoading(true);
                    try {
                      const params = new URLSearchParams({ count: '20', category: selectedCategory !== 'all' ? selectedCategory : '', midgame: 'true' });
                      const resp = await axios.get(`/api/rules/random?${params}`, { headers: buildHeaders() });
                      if (resp && Array.isArray(resp.data)) setSearchResults(resp.data);
                    } catch (e) {
                      warn('Browse failed', e && e.message);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="phb-btn phb-btn-secondary"
                >
                  Browse
                </button>
              </div>
            </div>
          </form>

          {/* Quick Rules */}
          <div style={{ marginTop: '1.5rem' }}>
            <h3 className="phb-section-header">Midgame Reference</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {quickRules.map(rule => (
                <button
                  key={rule}
                  onClick={() => handleQuickSearch(rule)}
                  className="phb-tag"
                >
                  {rule}
                </button>
              ))}
            </div>
          </div>

          {/* Table Situations */}
          <div style={{ marginTop: '1.5rem' }}>
            <h3 className="phb-section-header">Table Situations</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {midgameCategories.map(cat => (
                <button
                  key={cat.id}
                  onClick={async () => {
                    setSelectedCategory(cat.id);
                    await fetchRandom(cat.id, 12);
                  }}
                  className="phb-tag"
                  style={{ background: 'linear-gradient(to bottom, #4a3728, #3d2b1f)', color: '#d4c4a0', borderColor: '#6b4f3a' }}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Browse by Category */}
          <div style={{ marginTop: '1.5rem' }}>
            <h3 className="phb-section-header">Browse by Category</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {ruleCategories.map(cat => (
                <button
                  key={cat.id}
                  onClick={async () => {
                    const cid = cat.id;
                    setSelectedCategory(cid);
                    await fetchRandom(cid, browseCount);
                  }}
                  className={`phb-tag ${selectedCategory === cat.id ? 'phb-tag-active' : ''}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Recent Searches */}
          {recentSearches.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <h3 className="phb-section-header" style={{ fontSize: '0.85rem' }}>Recent Searches</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {recentSearches.map((query, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickSearch(query)}
                    className="phb-tag"
                    style={{ opacity: 0.7 }}
                  >
                    {query}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div>
            <h2 className="phb-h2">Search Results ({searchResults.length})</h2>

            <div>
              {searchResults.map((rule, index) => (
                <div
                  key={rule.id || index}
                  className="phb-result-card"
                  onClick={() => openRule(rule)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <h3 className="phb-result-title">{rule.title}</h3>
                    <div className="phb-result-meta">
                      {rule.category && (
                        <span className="phb-result-badge phb-badge-category">{rule.category}</span>
                      )}
                      {rule.page && (
                        <span className="phb-result-badge phb-badge-page">p.{rule.page}</span>
                      )}
                      {rule.source && (
                        <span className="phb-result-badge phb-badge-source">{rule.sourceAbbr || rule.source}</span>
                      )}
                      {rule.sourceMethod && (
                        <span className="phb-result-badge phb-badge-confidence">{rule.sourceMethod}</span>
                      )}
                      {rule.confidence != null && (
                        <span className="phb-result-badge phb-badge-confidence">{Math.round(Number(rule.confidence) * 100)}%</span>
                      )}
                    </div>
                  </div>
                  {rule.summary && (
                    <p className="phb-result-summary">{rule.summary}</p>
                  )}

                  <div>
                    <RuleContent content={rule.content} query={searchQuery} compact />
                    <RuleExample example={rule.examples} query={searchQuery} compact />
                  </div>
                </div>
              ))}
            </div>

            {/* Show more for browse results */}
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button
                onClick={async () => {
                  const next = browseCount + 20;
                  setBrowseCount(next);
                  await fetchRandom(selectedCategory, next);
                }}
                className="phb-btn phb-btn-primary"
              >
                Show more
              </button>
            </div>
          </div>
        )}

        {/* Rule Detail Modal */}
        {selectedRule && (
          <div className="phb-modal-overlay" style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 50 }}>
            <div className="phb-modal">
              <button
                className="phb-modal-close"
                onClick={() => setSelectedRule(null)}
              >
                ×
              </button>
              <h2 className="phb-h2" style={{ marginTop: 0 }}>{selectedRule.title}</h2>

              <div className="phb-result-meta">
                {selectedRule.category && (
                  <span className="phb-result-badge phb-badge-category">{selectedRule.category}</span>
                )}
                {selectedRule.page && (
                  <span className="phb-result-badge phb-badge-page">Page {selectedRule.page}{selectedRule.pageEnd && selectedRule.pageEnd !== selectedRule.page ? `-${selectedRule.pageEnd}` : ''}</span>
                )}
                {selectedRule.source && (
                  <span className="phb-result-badge phb-badge-source">{selectedRule.sourceAbbr || selectedRule.source}</span>
                )}
                {selectedRule.sourceMethod && (
                  <span className="phb-result-badge phb-badge-confidence">{selectedRule.sourceMethod}</span>
                )}
                {selectedRule.confidence != null && (
                  <span className="phb-result-badge phb-badge-confidence">Confidence {Math.round(Number(selectedRule.confidence) * 100)}%</span>
                )}
              </div>
              {selectedRule.summary && (
                <p className="phb-text" style={{ fontStyle: 'italic', marginBottom: '1rem' }}>{selectedRule.summary}</p>
              )}

              <RuleContent content={selectedRule.content} query={searchQuery} />
              <RuleExample example={selectedRule.examples} query={searchQuery} />

              {selectedRule.relatedRules && selectedRule.relatedRules.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, color: '#8b6914', marginBottom: '0.5rem' }}>Related Rules</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {selectedRule.relatedRules.map((related, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuickSearch(related)}
                        className="phb-tag"
                      >
                        {related}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* No Results */}
        {searchQuery && !loading && searchResults.length === 0 && (
          <div className="phb-no-results">
            <p>No rules found for "{searchQuery}"</p>
            <p style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>Try different keywords or check a different category</p>
          </div>
        )}
      </div>
    </section>
  );
}

export default RulesTab;